<template>
  <div class="jobs-filter-container">

    <b-row class="d-flex justify-content-between align-items-center mx-1">
      <div class="d-flex flex-row">
        <h4>
          <template v-if="loading">Loading...</template>
          <template v-else>{{ this.results.edges.length | plural('result') }}</template>
        </h4>
      </div>
      <div class="d-flex flex-row">
        <sort class="mr-4" :options="internalJobOrderByEnums" :value="order" @input="v => $emit('orderUpdated', v)"/>
        <toggle-filters class="py-1 px-3" v-model="showHorizontalWhere"/>
      </div>
    </b-row>

    <div id="internalJobList"></div>

    <b-row class="my-4" v-if="highlightedJob">
      <b-col>
        <job-list-item
          :isProjectDisplay="isProjectDisplay"
          :job="highlightedJob"
          :layout="layout"
          :isHighlighted="true"
          @selectJob="$event => { $emit('selectJob', $event); }"
          @closeHighlightedRole="hideHighlightedJob = true"
        />
      </b-col>
    </b-row>

    <b-card class="horizontal-where mt-3" v-if="showHorizontalWhere">
      <filter-where
        orientation="horizontal"
        :whereCustomFields="whereCustomFields"
        :value="where"
        @input="v => $emit('whereUpdated', v)"
      />
    </b-card>

    <loading-spinner class="my-5 text-center" v-if="loading"/>

    <template v-else-if="results && (layout === 'block' || layout === 'select-modal')">
      <template v-if="total">
        <b-row class="mt-3">
          <b-col
            class="mb-4"
            cols="12"
            :lg="layout === 'select-modal' ? 12 : 4"
            v-for="{ entry } in results.edges.filter(({ entry }) => entry && entry.id)"
            :key="entry.id"
          >
            <job-list-item
              :isProjectDisplay="isProjectDisplay"
              :job="entry"
              :layout="layout"
              :displayDetachButton="displayDetachButton"
              @detachJob="$emit('detachJob', $event)"
              @importJob="$event => { $emit('importJob', $event); }"
            />
          </b-col>
        </b-row>
        <div v-if="!hidePagination && page && totalPages" class="filter-pagination">
          <p>Page {{ page }} of {{ totalPages }}</p>
          <b-pagination :value="page" @input="v => $emit('pageUpdated', v)" :per-page="limit" :total-rows="total"/>
        </div>
      </template>
      <div class="mt-3" v-else>
        <p class="text-center">No results</p>
      </div>
    </template>

    <template v-else-if="results && layout === 'page'">
      <template v-if="total">
        <b-row class="mt-3 row-cols-1 row-cols-md-2">
          <b-col
            class="mb-4"
            cols="12"
            lg="6"
            v-for="{ entry } in results.edges.filter(({ entry }) => entry && entry.id)"
            :key="entry.id"
          >
            <job-list-item
              class="h-100"
              :isProjectDisplay="isProjectDisplay"
              :job="entry"
              :displayDetachButton="displayDetachButton"
              @detachJob="$emit('detachJob', $event)"
            />
          </b-col>
        </b-row>
        <div v-if="!hidePagination && page && totalPages" class="filter-pagination">
          <p>Page {{ page }} of {{ totalPages }}</p>
          <b-pagination :value="page" @input="v => $emit('pageUpdated', v)" :per-page="limit" :total-rows="total"/>
        </div>
      </template>
      <div class="mt-3" v-else>
        <p class="text-center">No results</p>
      </div>
    </template>
  </div>
</template>

<script>
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import JobListItem from '@/components/jobs/UserMatchListItem';
import FilterWhere from '@/components/jobs/FilterWhere';
import Sort from '@/components/misc/Sort';
import ToggleFilters from '@/components/misc/ToggleFilters';
import enumsMixin from '@/mixins/enums';
import { mapRoot } from '@src/mixins/root';

export default {
  name: 'ProjectsMatchedJobsFilter',
  props: {
    layout: {
      type: String,
      default: () => 'page',
      validate: layout => [ 'block', 'page', 'select-modal' ].includes(layout),
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    highlightedID: {
      type: String,
      default: null,
    },
    isProjectDisplay: {
      type: Boolean,
      default: () => false,
    },
    hidePagination: {
      type: Boolean,
      default: () => false,
    },
    defaultShowFilters: {
      type: Boolean,
      default: () => true,
    },
    where: {
      type: Object,
      default: () => ({}),
    },
    whereCustomFields: {
      type: Object,
      default: () => ({}),
    },
    order: {
      type: String,
      default: () => null,
    },
    results: {
      type: Object,
      default: () => ({
        edges: [],
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      }),
      validate: ({ edges }) => Array.isArray(edges),
    },
    selectedContactIDs: {
      type: Array,
      default: () => ([]),
    },
    displayDetachButton: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Sort,
    ToggleFilters,
    FilterWhere,
    LoadingSpinner,
    JobListItem,
  },
  mixins: [
    enumsMixin,
  ],
  data() {
    return {
      showHorizontalWhere: this.defaultShowFilters === true,
      hideHighlightedJob: false,
    };
  },
  computed: {
    ...mapRoot([ 'isLoggedIn' ]),
    highlightedJob() {
      if (!this.hideHighlightedJob && this.highlightedID && this.results && Array.isArray(this.results.edges)) {
        // @TODO: move this logic to the index file
        const highlightedJobObj = this.results.edges.find(({ entry: { id } = {} }) => id === this.highlightedID);
        return highlightedJobObj ? highlightedJobObj.entry : null;
      }
      return null;
    },
    page() {
      const { page } = this.results || {};
      return page || 1;
    },
    limit() {
      const { limit } = this.results || {};
      return limit || 10;
    },
    total() {
      const { total } = this.results || {};
      return total || 0;
    },
    totalPages() {
      const { totalPages } = this.results || {};
      return totalPages || 1;
    },
  },
};
</script>

<style lang="scss">
.jobs-filter-container {

  .card.horizontal-where {
    margin-top: 10px;
    margin-bottom: 20px;
  }

  .filter-pagination {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .vertical-where {
    .vertical-sort {
      padding: 10px 20px 0 20px;

      label {
        font-weight: normal;
      }

      .custom-radio {
        margin-right: 0;
        margin-bottom: 10px;
      }
    }

    .results-tag {
      color: rgba(49, 55, 67, 0.5);
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/projects/InternalJobsListContainer.vue